// External Imports
import { useSearchParams } from "react-router-dom";
import Heading from "../UI/Heading";
import { useContext, useEffect, useState, Fragment } from "react";
import { UtilityContext } from "../../context/util-context";
import DetailPair from "../UI/DetailPair";

// Internal Imports
import LinkPair from "../UI/LinkPair";
import uploadFile from "../../util/upload-file";
import CreditApplication from "./CreditApplication";
import {
  MdCarCrash,
  MdCardGiftcard,
  MdCheck,
  MdClose,
  MdDirectionsCar,
  MdEdit,
  MdPayments,
  MdRefresh,
} from "react-icons/md";
import { requestAdminAPI } from "../../api/posh-api";
import { getISODate, isoDateToString } from "../../util/format-date";
import PersonCard from "../UI/PersonCard";
import AddApplication from "../applications-page/AddApplication";
import PartnerRevenue from "./PartnerRevenue";
import Notes from "../UI/Notes";
import { formatCurrency } from "../../util/format-text";
import BankAccounts from "./BankAccounts";
import AuditLogs from "../UI/AuditLogs";

/** ========================================================================
 * * Partner Information Page
 * Renders a page, which displays all information about a partner.
 * ========================================================================== */

export default function PartnerInfo() {
  // Load Context
  const { navigate } = useContext(UtilityContext);

  // Get Partner ID from URL
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  // Initialize States
  const [partner, setPartner] = useState({});
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(false);
  const [add, setAdd] = useState(false);
  const [metrics, setMetrics] = useState({});
  const [uploading, setUploading] = useState(false);

  /* ======================= FETCH PARTNER ======================= */
  const fetchPartner = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("fetch", {
        table: "partner",
        id: id,
        columns: [
          "applications: applications_on_partner { avgValue highValue lowValue financeAmount lastValueCheck lastValueMileage valueCertainty depreciationClaimed monthlyPayment onboarded vehicle { activeBookingId }}",
          "createdBy { id name title photo }",
        ],
      });
      const newMetrics = {
        onboarded: 0,
        notOnboarded: 0,
        booked: 0,
        totalMonthlyPayment: 0,
        utilization: 0,
        totalAssetsValue: 0,
        totalLoanAmount: 0,
      };
      response.applications.forEach((app) => {
        if (app.onboarded) {
          newMetrics.onboarded += 1;
          if (app.vehicle?.activeBookingId) newMetrics.booked += 1;
          if (app.monthlyPayment)
            newMetrics.totalMonthlyPayment += app.monthlyPayment;
          if (app.avgValue) newMetrics.totalAssetsValue += app.avgValue;
          if (app.financeAmount)
            newMetrics.totalLoanAmount += app.financeAmount;
        } else newMetrics.notOnboarded += 1;
      });
      if (newMetrics.onboarded > 0) {
        if (newMetrics.booked > 0)
          newMetrics.utilization =
            (newMetrics.booked / newMetrics.onboarded) * 100;
      }
      setMetrics(newMetrics);
      setPartner(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // ========================================= EDIT PARTNER
  const editHandler = async () => {
    try {
      setLoading(true);
      const payload = {
        partnerId: partner.id,
        phone: document.getElementById("phone").value,
        address: document.getElementById("address").value,
        dob:
          document.getElementById("dob").value &&
          getISODate(document.getElementById("dob").value),
      };
      const docs = ["driversLicenseFront", "driversLicenseBack", "agreement"];
      for (let doc of docs) {
        if (document.getElementById(doc).files[0]) {
          payload[doc] = await uploadFile(
            `partner/${partner.id}/${doc}`,
            document.getElementById(doc).files[0],
            setUploading,
          );
        }
      }
      await requestAdminAPI("edit-partner", payload);
      fetchPartner();
      setEdit(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartner();
  }, []);

  if (!id || (!loading && (!partner || !partner?.id)))
    return (
      <div className="container">
        <p className="text-stone-400">
          Partner ID Not Provided or Partner Not Found!
        </p>
      </div>
    );

  return (
    <div className="divider">
      <AddApplication open={add} setOpen={setAdd} />
      <div className="container gap-8 lg:col-span-3">
        <Heading title="Partner Information" id={id}>
          <AuditLogs table="partner" id={id} />
          <MdRefresh className="icon" onClick={fetchPartner} />
        </Heading>
        <div className="grid-cols-5 info">
          <DetailPair
            label="Total Applications"
            value={`${metrics.onboarded}/${partner.applications?.length || 0}`}
            loading={loading}
          />
          <DetailPair
            label="Utilization"
            value={`${metrics.utilization?.toFixed(0)}% (${metrics.booked}/${
              metrics.onboarded
            })`}
            loading={loading}
          />
          <DetailPair
            label="Monthly Payment"
            value={formatCurrency(metrics.totalMonthlyPayment)}
            loading={loading}
          />
          <DetailPair
            label="Assets Value"
            value={formatCurrency(metrics.totalAssetsValue)}
            loading={loading}
          />
          <DetailPair
            label="Loan Amount"
            value={formatCurrency(metrics.totalLoanAmount)}
            loading={loading}
          />
        </div>
        <PartnerRevenue partnerId={partner.id} />
        <CreditApplication partner={partner} refresh={fetchPartner} />
        <BankAccounts partner={partner} />
        <Notes table="partner" id={partner.id} />
      </div>
      <div className="sidebar">
        <div className="grid gap-8 overflow-scroll no-scrollbar">
          <div className="flex items-center justify-around space-x-4">
            {edit ? (
              <Fragment>
                <MdCheck onClick={editHandler} className="icon" />
                <MdClose onClick={() => setEdit(false)} className="icon" />
              </Fragment>
            ) : (
              <Fragment>
                <MdEdit onClick={() => setEdit(true)} className="icon" />
              </Fragment>
            )}
          </div>
          <div className="grid gap-6 text-sm">
            <PersonCard
              label="Partner:"
              name={partner.businessName || partner.name}
              secondary={partner.type?.toUpperCase()}
              image={partner.photo}
              loading={loading}
            />
            {partner.type === "business" && (
              <DetailPair
                label="Authorized Signatory"
                value={partner.name}
                loading={loading}
              />
            )}
            <DetailPair label="Email" value={partner.email} loading={loading} />
            <DetailPair
              label="Phone"
              value={partner.phone}
              edit={edit}
              loading={loading}
              inputOptions={{
                type: "text",
                id: "phone",
                required: true,
                defaultValue: partner.phone,
              }}
            />
            {partner.type === "business" && (
              <DetailPair
                label="Business Address"
                value={partner.businessAddress}
                edit={edit}
                loading={loading}
                inputOptions={{
                  type: "text",
                  id: "businessAddress",
                  required: true,
                  defaultValue: partner.businessAddress,
                }}
              />
            )}
            <DetailPair
              label="Address"
              value={partner.address}
              edit={edit}
              loading={loading}
              inputOptions={{
                type: "text",
                id: "address",
                required: true,
                defaultValue: partner.address,
              }}
            />
            <div className="grid gap-2 lg:grid-cols-2">
              <LinkPair
                label="Driver's License"
                link={partner.driversLicenseFront}
                value="Front"
                edit={edit}
                loading={loading}
                inputOptions={{
                  type: "file",
                  id: "driversLicenseFront",
                  accept: ".pdf,.jpg,.jpeg,.png",
                }}
              />
              <LinkPair
                label="Driver's License"
                link={partner.driversLicenseBack}
                value="Back"
                edit={edit}
                loading={loading}
                inputOptions={{
                  type: "file",
                  id: "driversLicenseBack",
                  accept: ".pdf,.jpg,.jpeg,.png",
                }}
              />
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
              <DetailPair
                label="Date of Birth"
                value={isoDateToString(partner.dob, "MM/dd/yyyy")}
                edit={edit}
                loading={loading}
                inputOptions={{
                  type: "date",
                  id: "dob",
                  defaultValue: isoDateToString(partner.dob, "yyyy-MM-dd"),
                }}
              />
              <LinkPair
                label="Agreement"
                link={partner.agreement}
                edit={edit}
                loading={loading}
                inputOptions={{
                  type: "file",
                  id: "agreement",
                  accept: ".pdf,.jpg,.jpeg,.png",
                }}
              />
            </div>
            {partner.createdBy && (
              <PersonCard
                label="Created by:"
                name={partner.createdBy?.name}
                secondary={isoDateToString(
                  partner.dateCreated,
                  "MM/dd/yyyy hh:mm a",
                )}
                image={partner.createdBy?.photo}
                link={`/employees/info?id=${partner.createdBy?.id}`}
                loading={loading}
              />
            )}
          </div>
        </div>

        <div className="grid gap-4">
          <div className="grid grid-cols-4 gap-2 justify-items-center">
            <MdDirectionsCar
              className="icon"
              onClick={() =>
                navigate(`/applications?status=onboarded&query=${partner.id}`)
              }
              title="View Onboarded Vehicles"
            />
            <MdCarCrash
              className="icon"
              onClick={(e) =>
                navigate(
                  `/applications?status=not%20onboarded&query=${partner.id}`,
                  e,
                )
              }
              title="View Inactive Vehicles"
            />
            <MdPayments
              className="icon"
              onClick={(e) =>
                navigate(
                  `/expenses?query=${partner.id}&status=succeeded&category=application-installment-64200`,
                  e,
                )
              }
              title="View Installments"
            />
            <MdCardGiftcard
              className="icon"
              onClick={(e) =>
                navigate(
                  `/expenses?query=${partner.id}&status=succeeded&category=partner-rewards-64200`,
                  e,
                )
              }
              title="View Partner Credits"
            />
          </div>
          <div onClick={() => setAdd(true)} className="btn-primary">
            Add Vehicle Application
          </div>
        </div>
      </div>
    </div>
  );
}
